import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React from 'react';
import { canUseDOM } from 'exenv';
import ScrollLock from 'react-scrolllock';
import scrollIntoView from 'scroll-into-view-if-needed';
import { fg } from '@atlaskit/platform-feature-flags';
import Portal from '@atlaskit/portal';
import { layers } from '@atlaskit/theme/constants';
import { ElementBox } from '../utils/use-element-box';
import { Fade } from './animation';
import Clone from './clone';
import NodeResolverSpotlightInner from './node-resolver-spotlight-inner';
import SpotlightDialog from './spotlight-dialog';
import { SpotlightTransitionConsumer } from './spotlight-transition';
/**
 * __Spotlight inner__
 *
 * Renders the spotlight target clone and dialog.
 *
 * @internal
 */
class SpotlightInner extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      // This is only used when targetReplacement is specified.
      // In this case, we have to render the targetReplacement component,
      // get a dom reference from that component, then render again passing
      // that reference into SpotlightDialog (Popper).
      replacementElement: null
    });
    _defineProperty(this, "getTargetNodeStyle", box => {
      if (!canUseDOM) {
        return {};
      }
      return {
        ...box,
        position: 'fixed'
      };
    });
  }
  componentDidUpdate(prevProps) {
    if (prevProps.targetNode !== this.props.targetNode) {
      scrollIntoView(this.props.targetNode, {
        scrollMode: 'if-needed',
        block: this.props.scrollPositionBlock
      });
    }
  }
  componentDidMount() {
    scrollIntoView(this.props.targetNode, {
      scrollMode: 'if-needed',
      block: this.props.scrollPositionBlock
    });
    this.props.onOpened();
  }
  componentWillUnmount() {
    this.props.onClosed();
  }
  render() {
    const {
      pulse,
      target,
      targetNode,
      targetBgColor,
      targetOnClick,
      targetRadius,
      testId,
      targetReplacement: TargetReplacement
    } = this.props;
    const {
      replacementElement
    } = this.state;
    return /*#__PURE__*/React.createElement(SpotlightTransitionConsumer, null, ({
      isOpen,
      onExited
    }) => /*#__PURE__*/React.createElement(Portal, {
      zIndex: layers.spotlight() + 1
    }, TargetReplacement ? /*#__PURE__*/React.createElement(NodeResolverSpotlightInner, {
      hasNodeResolver: !fg('platform-design-system-dsp-20262-spotlight-inner'),
      innerRef: elem => this.setState({
        replacementElement: elem
      })
    }, /*#__PURE__*/React.createElement(ElementBox, {
      element: targetNode
    }, box => /*#__PURE__*/React.createElement(TargetReplacement, _extends({
      "data-testid": `${testId}--target`
    }, this.getTargetNodeStyle(box))))) : /*#__PURE__*/React.createElement(ElementBox, {
      element: targetNode
    }, box => /*#__PURE__*/React.createElement(Clone, {
      testId: `${testId}--target`,
      pulse: pulse,
      target: target
      // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
      ,
      style: this.getTargetNodeStyle(box),
      targetBgColor: targetBgColor,
      targetNode: targetNode,
      targetOnClick: targetOnClick,
      targetRadius: targetRadius
    })), TargetReplacement && !replacementElement ? null : /*#__PURE__*/React.createElement(Fade, {
      hasEntered: isOpen,
      onExited: onExited
    }, animationStyles => /*#__PURE__*/React.createElement(SpotlightDialog, {
      testId: `${testId}--dialog`,
      actions: this.props.actions,
      actionsBeforeElement: this.props.actionsBeforeElement,
      children: this.props.children,
      dialogPlacement: this.props.dialogPlacement,
      dialogWidth: this.props.dialogWidth,
      footer: this.props.footer,
      header: this.props.header,
      heading: this.props.heading,
      headingAfterElement: this.props.headingAfterElement,
      image: this.props.image,
      targetNode: replacementElement || targetNode,
      animationStyles: animationStyles
    })), /*#__PURE__*/React.createElement(ScrollLock, null)));
  }
}
_defineProperty(SpotlightInner, "defaultProps", {
  dialogWidth: 400,
  pulse: true
});
export default SpotlightInner;